package diana.soleil.design.iraniancanadienbazzar.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsParams
import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.LocationController
import diana.soleil.design.iraniancanadienbazzar.core.model.Metros
import diana.soleil.design.iraniancanadienbazzar.core.model.categoryIconKey
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.OnboardingFlowScreen
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.OnboardingMetroOption
import diana.soleil.design.iraniancanadienbazzar.feature.onboarding.OnboardingStep
import diana.soleil.design.iraniancanadienbazzar.locale.LocaleController
import kotlin.time.TimeSource
import org.koin.compose.koinInject

/**
 * Host for the first-run onboarding wizard (docs/ONBOARDING_PLAN.md): wires the flow screen to the
 * locale/city/interests controllers, the public categories collection (interest options), location
 * (nearest-metro), the analytics funnel, and the completion flag. Shown only when
 * `AppPreferences.onboardingComplete` is false and no session exists (see AppNavHost).
 */
@Composable
fun OnboardingRoute(onFinished: () -> Unit) {
    val locale = koinInject<LocaleController>()
    val city = koinInject<CityController>()
    val location = koinInject<LocationController>()
    val analytics = koinInject<AnalyticsTracker>()
    val preferences = koinInject<AppPreferences>()

    val language by locale.language.collectAsState()
    val selectedCountry by city.activeCountry.collectAsState()
    val selectedMetroId by city.activeMetroId.collectAsState()
    val launchedMetros by city.launchedMetros.collectAsState()

    // City step lists only the active country's metros (docs/US_EXPANSION_PLAN.md) — recomputes when
    // the user changes country on the preceding step.
    val metroOptions = remember(language, launchedMetros, selectedCountry) {
        Metros.forCountry(selectedCountry).map { metro ->
            OnboardingMetroOption(
                id = metro.id,
                label = metro.name.resolve(language),
                sublabel = metro.province,
                enabled = city.isLaunched(metro.id),
            )
        }
    }

    val tracked = remember { mutableSetOf<String>() }
    // Monotonic wizard-start mark (survives recomposition) → onboarding_complete carries duration_s.
    val startMark = remember { TimeSource.Monotonic.markNow() }
    OnboardingFlowScreen(
        currentLanguage = language,
        onLanguageSelected = locale::setLanguage,
        selectedCountry = selectedCountry,
        availableCountries = Metros.launchedCountries(launchedMetros),
        onCountrySelected = city::setCountry,
        metroOptions = metroOptions,
        selectedMetroId = selectedMetroId,
        onMetroSelected = city::setMetro,
        resolveNearestMetro = {
            location.ensureLocation()
            val fix = location.location.value
            if (fix == null) {
                null
            } else {
                // Nearest launched metro IN THE ACTIVE COUNTRY by squared equirectangular distance —
                // plenty at metro scale; scoping to the country keeps "use my location" consistent
                // with the country the user just chose.
                Metros.forCountry(city.activeCountry.value)
                    .filter { city.isLaunched(it.id) }
                    .minByOrNull { metro ->
                        val dLat = metro.centerLat - fix.lat
                        val dLng = metro.centerLng - fix.lng
                        dLat * dLat + dLng * dLng
                    }
                    ?.id
            }
        },
        onStepShown = { step ->
            // Each step tracked once per wizard run; language is stamped when the user moves PAST
            // the language step (i.e. their choice stuck).
            if (tracked.add(step.name)) {
                when (step) {
                    OnboardingStep.Language -> analytics.track(AnalyticsEvents.ONBOARDING_START)
                    OnboardingStep.Country, OnboardingStep.City -> {
                        if (tracked.add("language_stamped")) {
                            analytics.track(
                                AnalyticsEvents.ONBOARDING_LANGUAGE,
                                mapOf(AnalyticsParams.LANGUAGE to locale.language.value.code),
                            )
                        }
                        if (step == OnboardingStep.City) {
                            analytics.track(
                                AnalyticsEvents.ONBOARDING_COUNTRY,
                                mapOf(AnalyticsParams.COUNTRY to city.activeCountry.value.code),
                            )
                        }
                    }
                }
            }
        },
        onFinish = {
            analytics.track(
                AnalyticsEvents.ONBOARDING_CITY,
                mapOf(
                    AnalyticsParams.METRO_ID to
                        (city.activeMetroId.value ?: "all_${city.activeCountry.value.code.lowercase()}"),
                ),
            )
            analytics.track(
                AnalyticsEvents.ONBOARDING_COMPLETE,
                mapOf(AnalyticsParams.DURATION_S to startMark.elapsedNow().inWholeSeconds.toInt()),
            )
            preferences.onboardingComplete = true
            onFinished()
        },
    )
}
